import React from "react";
import PropTypes from "prop-types";
import { SOURCE_META, DEFAULT_SOURCE_ICON, formatNotifyTime, channelLabel } from "../../../helpers/notifyMeta";
import { toSolid } from "../../../helpers/moduleIcons";

/**
 * 通知樣式 primitive（Figma DCK-00「下拉選單 > notice」）。
 *
 * 版面(全部取自設計稿 CSS):
 *   notice(row, space-between)
 *     └ Frame 80(column)
 *         ├ Frame 21(row, gap 5)  = 未讀點 8px + Frame 79(row, gap 17)= icon 24 + 標題
 *         └ Frame 22(padding-left 40, gap 8)= 內文 + Frame 23(row, gap 5)= 時鐘 + 時間
 *
 * 圖示取自 helpers/notifyMeta(與側欄選單同一份),但改用實心版:
 * 側欄是深底細線,notice 是藍圓底白字符,設計稿指定 bxs-*。
 *
 * 鈴鐺下拉與訊息中心共用同一組元件,避免兩邊樣式漂移。
 *
 *   <NoticeItem source="審核訊息" message="…" time={iso} unread />
 */

/** 來源模組圓底圖示(24px、Blue/300、白色實心字符 16px) */
export const NoticeIcon = ({ source, className }) => {
  const icon = (SOURCE_META[source] || {}).icon || DEFAULT_SOURCE_ICON;
  return (
    <span className={`dck-notice-icon ${className || ""}`} aria-hidden="true">
      <i className={`bx ${toSolid(icon)}`} />
    </span>
  );
};
NoticeIcon.propTypes = { source: PropTypes.string, className: PropTypes.string };

/** 單筆通知 */
export const NoticeItem = ({ source, title, message, docNo, time, channel, unread, meta, extra, actions, className, boxed }) => (
  // 未讀=淺藍底+紅點+藍圈;已讀=白底+灰圈(2128:29125/29127)
  <div
    className={`dck-notice ${unread ? "dck-notice--unread" : "dck-notice--read"}${boxed ? " dck-notice--boxed" : ""} ${className || ""}`}
  >
    <div className="dck-notice-body">
      <div className="dck-notice-head">
        {/* boxed(訊息查詢)版:已讀也要留住這 8px 的位置 —— 稿的兩張 notice
            都有 Ellipse3,少了它已讀那幾筆的圖示會往左跳 13px,整份清單對不齊。
            鈴鐺下拉維持原樣(內文縮排 40 是對齊 icon 算的,補了佔位反而會歪)。 */}
        {unread || boxed ? (
          <span
            className={`dck-notice-dot${unread ? "" : " is-read"}`}
            aria-label={unread ? "未讀" : undefined}
            aria-hidden={unread ? undefined : "true"}
          />
        ) : null}
        <div className="dck-notice-title-row">
          <NoticeIcon source={source} />
          <span className="dck-notice-title">{title || source}</span>
          {extra}
        </div>
      </div>
      <div className="dck-notice-content">
        <div className="dck-notice-message">
          {docNo ? `${docNo}｜` : ""}{message}
        </div>
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          {meta}
          <div className="dck-notice-time">
            <i className="bx bx-time" />
            {/* 通知管道跟在時間後面(WF_v3 2375:36326:「1 小時前 ｜ 站內 & Email 通知」);
                資料值是全形「站內＆Email」,顯示層在這裡統一半形化(票 25),呼叫端傳原值即可 */}
            <span>{formatNotifyTime(time)}{channel ? `　｜　${channelLabel(channel)} 通知` : ""}</span>
          </div>
        </div>
      </div>
    </div>
    {actions ? <div className="dck-notice-action">{actions}</div> : null}
  </div>
);
NoticeItem.propTypes = {
  source: PropTypes.string,
  title: PropTypes.node,
  message: PropTypes.node,
  docNo: PropTypes.string,
  time: PropTypes.string,
  // 通知管道(資料值 站內＆Email/站內/Email),顯示在時間後面;全形＆由 channelLabel 轉半形
  channel: PropTypes.string,
  unread: PropTypes.bool,
  // 時間列最前面的小標(如紅色「到期」tag)
  meta: PropTypes.node,
  extra: PropTypes.node,
  actions: PropTypes.node,
  className: PropTypes.string,
  /**
   * 訊息查詢那種「一筆一塊」的版型(2128:29125/29127):自己有 10px 內距,
   * 內文縮排 53(未讀點 8 + gap 5 + icon 24 + gap 17)。
   * 鈴鐺下拉不傳這個 —— 下拉的間距由選單的 list(gap 15/padding 10)負責,
   * 內文縮排 40(下拉的內文對齊 icon,不含未讀點)。
   */
  boxed: PropTypes.bool,
};

export default NoticeItem;
